import { useState, useEffect } from 'react';

import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../../backend/src/organisation/organisation-permission';
import { useUpdateAdminApprovalLimit } from '@/api/mutations/useUpdateAdminApprovalLimit';
import { useUpdateTokenLimit } from '@/api/mutations/useUpdateTokenLimit';
import { useUserContext } from '@/Contexts/User';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';

export function CreditLimits() {
  const { user } = useUserContext();
  const organisation = user?.organisation;
  const updateTokenLimitMutation = useUpdateTokenLimit();

  const [tokenLimit, setTokenLimit] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [higherApprovalLimit, setHigherApprovalLimit] = useState<string>('');
  const [isAdminApprovalLimitLoading, setIsAdminApprovalLimitLoading] = useState(false);
  const [hasUnsavedReviewerChanges, setHasUnsavedReviewerChanges] = useState(false);

  const { isEnabled } = useOrgPermissions();
  const { hasPermission } = useHasPermission();
  const isAdminApprovalEnabled = isEnabled(OrgPermission.AdminApproval);
  const updateAdminApprovalLimitMutation = useUpdateAdminApprovalLimit();

  // Initialize values when organisation data is loaded
  useEffect(() => {
    if (organisation?.adminApprovalTokenLimit !== undefined && organisation.adminApprovalTokenLimit !== null) {
      setHigherApprovalLimit(organisation.adminApprovalTokenLimit.toString());
    }
    if (organisation?.tokenApprovalLimit !== undefined && organisation.tokenApprovalLimit !== null) {
      setTokenLimit(organisation.tokenApprovalLimit.toString());
    }
  }, [organisation]);

  const handleHigherApprovalLimitChange = (value: string) => {
    setHigherApprovalLimit(value);
    const originalValue = organisation?.adminApprovalTokenLimit?.toString() || '';
    setHasUnsavedReviewerChanges(value !== originalValue);
  };

  const handleHigherApprovalLimitSave = async () => {
    setIsAdminApprovalLimitLoading(true);
    try {
      const adminApprovalTokenLimit = higherApprovalLimit ? parseInt(higherApprovalLimit, 10) : undefined;

      if (!adminApprovalTokenLimit) {
        toast.error('Please enter a valid approval limit');
        return;
      }

      await updateAdminApprovalLimitMutation.mutateAsync({
        adminApprovalTokenLimit,
      });

      toast.success('Approval limit updated successfully');
      setHasUnsavedReviewerChanges(false);
    } catch (error) {
      toast.error('Failed to save approval limit');
    } finally {
      setIsAdminApprovalLimitLoading(false);
    }
  };

  const handleTokenLimitChange = (value: string) => {
    setTokenLimit(value);
    const originalValue = organisation?.tokenApprovalLimit?.toString() || '';
    setHasUnsavedChanges(value !== originalValue);
  };

  const handleTokenLimitSave = async () => {
    setIsLoading(true);
    try {
      const tokenApprovalLimit = parseInt(tokenLimit, 10);
      if (isNaN(tokenApprovalLimit) || tokenApprovalLimit < 0) {
        toast.error('Please enter a valid credit limit');
        setIsLoading(false);
        return;
      }

      await updateTokenLimitMutation.mutateAsync({ tokenApprovalLimit });

      toast.success('Credit limit updated successfully');
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to save credit limit');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-10 py-6">
      <div className="">
        <div className="mb-8 flex items-center gap-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100">
            <Icon className="lucide--coins h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-serif text-base font-semibold text-gray-800">Credit Usage Limits</h2>
            <p className="text-sm text-gray-600">Configure limits at which your users will need to approve uploads.</p>
          </div>
        </div>

        {hasPermission(Permission.TokenLimitsManage) ? (
          <>
            <div className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="token-limit" className="text-sm font-medium text-gray-700">
                  Credit Approval Limit
                </Label>
                <Input
                  id="token-limit"
                  type="number"
                  value={tokenLimit}
                  onChange={(e) => handleTokenLimitChange(e.target.value)}
                  placeholder="Enter credit limit"
                  min="0"
                  step="1000"
                />
                <p className="text-sm leading-relaxed text-gray-500">
                  Number of credits which can be uploaded per upload before users will have to explicitly click
                  'continue' before processing will happen.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-5">
              <div className="text-sm text-gray-500">
                {hasUnsavedChanges ? (
                  <span className="inline-flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                    You have unsaved changes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    All changes saved
                  </span>
                )}
              </div>

              <Button variant="primary" onClick={handleTokenLimitSave} disabled={isLoading || !hasUnsavedChanges}>
                <Icon className="lucide--save" />
                {isLoading ? 'Saving...' : 'Save Limit'}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-5 shadow-sm">
            <Icon className="lucide--info mt-0.5 h-5 w-5 text-gray-500" />
            <p className="text-sm leading-relaxed text-gray-600">
              You do not have permission to manage credit limits. Please contact your administrator or the Wexler team.
            </p>
          </div>
        )}
      </div>

      {isAdminApprovalEnabled && (
        <div>
          <div className="mb-8 flex items-center gap-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100">
              <Icon className="lucide--shield-ban h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-serif text-base font-semibold text-gray-800">Admin Approval Limit</h2>
              <p className="text-sm text-gray-600">Configure limit for uploads requiring additional approval.</p>
            </div>
          </div>

          {hasPermission(Permission.TokenLimitsManage) ? (
            <>
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label htmlFor="higher-approval-limit" className="text-sm font-medium text-gray-700">
                    Admin Approval Credit Limit (Optional)
                  </Label>
                  <Input
                    id="higher-approval-limit"
                    type="number"
                    value={higherApprovalLimit}
                    onChange={(e) => handleHigherApprovalLimitChange(e.target.value)}
                    placeholder="Enter admin approval limit"
                    min="0"
                    step="1000"
                  />
                  <p className="text-sm leading-relaxed text-gray-500">
                    Uploads exceeding this limit will require approval from designated reviewers. Leave empty to disable
                    admin approval.
                  </p>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Designated Reviewers</Label>

                  <p className="text-sm leading-relaxed text-gray-500">
                    In order to make someone a reviewer, you need to add the <b>runs:approve</b> permission to their
                    role. Administrators by default have this permission, but if you want to create a new role for
                    reviewers, you can do so in the{' '}
                    <NavLink to="/organisation-settings/roles" className="text-blue-600 underline hover:text-blue-700">
                      Roles
                    </NavLink>{' '}
                    section.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-5">
                <div className="text-sm text-gray-500">
                  {hasUnsavedReviewerChanges ? (
                    <span className="inline-flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                      You have unsaved changes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500"></div>
                      All changes saved
                    </span>
                  )}
                </div>

                <Button
                  variant="primary"
                  onClick={handleHigherApprovalLimitSave}
                  disabled={isAdminApprovalLimitLoading || !hasUnsavedReviewerChanges}
                >
                  <Icon className="lucide--save" />
                  {isAdminApprovalLimitLoading ? 'Saving...' : 'Save Limit'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-5 shadow-sm">
              <Icon className="lucide--info mt-0.5 h-5 w-5 text-gray-500" />
              <p className="text-sm leading-relaxed text-gray-600">
                You do not have permission to manage reviewer configuration. Please contact your administrator or the
                Wexler team.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
